// onboardingService.ts - localStorage helpers for onboarding state
import apiService from '../apiService';

const STORAGE_KEY = 'valulearn_onboarding';
const CURRENT_VERSION = 1;

interface EnabledTours {
  home: boolean;
  quizSetup: boolean;
  quiz: boolean;
  study: boolean;
  analytics: boolean;
}

// Cache for enabled tours from site settings
let enabledToursCache: EnabledTours | null = null;
let enabledToursCacheTimestamp = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

interface OnboardingState {
  completedTours: Record<string, boolean>;
  version: number;
}

function getDefaultState(): OnboardingState {
  return {
    completedTours: {},
    version: CURRENT_VERSION,
  };
}

function getState(): OnboardingState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return getDefaultState();
    }

    const parsed = JSON.parse(stored) as OnboardingState;

    // Handle version migrations if needed
    if (parsed.version !== CURRENT_VERSION) {
      // For now, just reset to default if version mismatch
      return getDefaultState();
    }

    return parsed;
  } catch {
    return getDefaultState();
  }
}

function saveState(state: OnboardingState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.warn('Failed to save onboarding state:', error);
  }
}

export const onboardingService = {
  /**
   * Fetch enabled tours from site settings (with caching)
   */
  async getEnabledTours(): Promise<EnabledTours> {
    const now = Date.now();

    // Return cached value if still valid
    if (enabledToursCache && (now - enabledToursCacheTimestamp) < CACHE_TTL) {
      return enabledToursCache;
    }

    try {
      const settings = await apiService.getSiteSettings();
      enabledToursCache = settings.enabledTours;
      enabledToursCacheTimestamp = now;
      return settings.enabledTours;
    } catch (error) {
      console.warn('Failed to fetch enabled tours, using defaults:', error);
      // Return all enabled as default if fetch fails
      return {
        home: true,
        quizSetup: true,
        quiz: true,
        study: true,
        analytics: true
      };
    }
  },

  /**
   * Check if a specific tour is enabled (async)
   */
  async isTourEnabled(tourId: string): Promise<boolean> {
    const enabledTours = await this.getEnabledTours();
    // Map tour IDs to the settings keys
    const tourKeyMap: Record<string, keyof EnabledTours> = {
      'tour-home': 'home',
      'tour-quiz-setup': 'quizSetup',
      'tour-quiz': 'quiz',
      'tour-study': 'study',
      'tour-analytics': 'analytics'
    };
    const key = tourKeyMap[tourId];
    if (!key) {
      // Unknown tour ID - default to enabled
      return true;
    }
    return enabledTours[key] !== false;
  },

  /**
   * Clear the enabled tours cache (call after admin updates settings)
   */
  clearEnabledToursCache(): void {
    enabledToursCache = null;
    enabledToursCacheTimestamp = 0;
  },

  /**
   * Get all completed tours
   */
  getCompletedTours(): Record<string, boolean> {
    const state = getState();
    return state.completedTours;
  },

  /**
   * Check if a specific tour has been completed
   */
  isTourCompleted(tourId: string): boolean {
    const state = getState();
    return state.completedTours[tourId] === true;
  },

  /**
   * Mark a tour as completed
   */
  markTourComplete(tourId: string): void {
    const state = getState();
    state.completedTours[tourId] = true;
    saveState(state);
  },

  /**
   * Reset a specific tour (allow replay)
   */
  resetTour(tourId: string): void {
    const state = getState();
    delete state.completedTours[tourId];
    saveState(state);
  },

  /**
   * Reset all tours (for testing or user request)
   */
  resetAllTours(): void {
    saveState(getDefaultState());
  },

  /**
   * Get the current version
   */
  getVersion(): number {
    return CURRENT_VERSION;
  },
};

export default onboardingService;
